import PercentageBar from "@components/Global/PercentageBar";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { YStack, Text, Label, XStack } from "tamagui";
import LottieView from "lottie-react-native";
import { useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { DefaultRootState } from "@reduxLocal/persistState";
import useGlobalUser from "@hooks/getGlobalUser";
import { useHasActiveSubscription } from "@hooks/useSubscriptionStatus";
import apis from "@api/api";
import { themeSelector } from '@reduxLocal/selectors/themeSelector';

interface UsageStatMonitorProps {
  compact?: boolean;
}

const UsageStatMonitor = ({ compact = true }: UsageStatMonitorProps) => {
  const queryClient = useQueryClient();

  // Get user context from Redux
  const user = useSelector((state: DefaultRootState) => state.promiseData.data?.userData);
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const globalId = useGlobalUser();
  const theme = themeSelector();

  // Check if user has active subscription
  // If active subscription, show counts instead of percentages
  const hasActiveSubscription = useHasActiveSubscription();

  // Create context-aware query key - use session-based identifier
  // The backend reads from req.session, so we need to ensure the query refetches
  // when the company changes, even though the session is updated server-side
  const userId = user?.id || globalId;
  const companyId = currentCompany?.id || null;
  const queryKey = ['statCount', userId, companyId];

  // Use useQuery to ensure proper refetching when context changes
  // IMPORTANT: The backend reads stats from req.session.userId and req.session.companyNumber
  // These come from the session cookie sent with each request (via withCredentials: true)
  // When a user switches companies, the switchCompany API updates the session on the backend
  // and returns updated cookies. This query will automatically use the updated session
  // because noMonitor instance sends cookies with every request.
  const { data, isFetching, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      // Backend endpoint: GET /api/user/stat-counts
      // Backend reads: req.session.userId and req.session.companyNumber
      // The noMonitor instance uses withCredentials: true, so cookies are sent automatically
      // This ensures we get stats for the current session context (user + company)
      const res = await apis.getStatCounts();
      return res;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes - shorter cache for accuracy
    refetchOnWindowFocus: true, // Refetch when window gains focus
    enabled: !!userId, // Only fetch if we have a user ID
  });

  console.log('UsageStatMonitor: useQuery State', { isFetching, data, error });

  // Use refs to prevent infinite loops when invalidating queries
  const prevCompanyIdRef = useRef<string | null>(null);
  const prevUserIdRef = useRef<string | undefined>(undefined);

  // Invalidate and refetch when company changes
  // This ensures we get fresh data after company switch completes
  // The query key includes companyId, so React Query will treat it as a new query
  // and fetch fresh data with the updated session cookie
  useEffect(() => {
    // Only invalidate if companyId or userId actually changed
    const companyIdChanged = prevCompanyIdRef.current !== companyId;
    const userIdChanged = prevUserIdRef.current !== userId;

    if (userId && (companyIdChanged || userIdChanged)) {
      console.log('UsageStatMonitor: Invalidate query - companyId or userId changed', {
        prevCompanyId: prevCompanyIdRef.current,
        currentCompanyId: companyId,
        prevUserId: prevUserIdRef.current,
        currentUserId: userId,
        companyIdChanged,
        userIdChanged,
      });
      // Update refs BEFORE invalidating to prevent re-triggering
      prevCompanyIdRef.current = companyId;
      prevUserIdRef.current = userId;

      // Invalidate all statCount queries to force refetch with new session context
      // This is important because the backend session might have been updated
      // but React Query might have cached the old response
      queryClient.invalidateQueries({
        queryKey: ['statCount'],
        refetchType: 'active' // Only refetch active queries
      });
    } else if (!userId) {
      // Reset refs when userId is cleared
      prevCompanyIdRef.current = null;
      prevUserIdRef.current = undefined;
    }
  }, [companyId, userId, queryClient]);
  console.log('UsageStatMonitor: useEffect ran with companyId:', companyId, 'userId:', userId);


  // Memoize companyMembers to prevent infinite loops from cache reference changes
  const companyMembers = useMemo(() => {
    const members = queryClient.getQueryData(["companyMembers"]);
    return Array.isArray(members) ? members : [];
  }, [queryClient, companyId]); // Re-memoize when company changes

  // Determine if we're in company context or user context
  const isCompanyContext = !!currentCompany;
  
  // Memoize contextName to prevent unnecessary recalculations
  const contextName = useMemo(() => {
    return isCompanyContext
      ? currentCompany?.companyName || 'Company'
      : user?.email || user?.profile_information?.name || 'User';
  }, [isCompanyContext, currentCompany?.companyName, user?.email, user?.profile_information?.name]);



  const stats = useMemo(() => {
    if (!data || Object.keys(data).length === 0) return [];

    const limitMap = {
      data: Number((data as any)?.limits?.data_limit) === 0 ? "∞" : Number((data as any)?.limits?.data_limit || 300),
      models: Number((data as any)?.limits?.data_folder_limit) === 0 ? "∞" : Number((data as any)?.limits?.data_folder_limit || 3),
      projects: Number((data as any)?.limits?.project_limit) === 0 ? "∞" : Number((data as any)?.limits?.project_limit || 1),
      users: Number((data as any)?.limits?.max_users) === 0 ? "∞" : Number((data as any)?.limits?.max_users || 3),
      storage: Number((data as any)?.limits?.storage_limit) === 0 ? "∞" : Number((data as any)?.limits?.storage_limit || 300),
      aiCall: Number((data as any)?.limits?.ai_call_limit) === 0 ? "∞" : Number((data as any)?.limits?.ai_call_limit || 100),
    };

    const getPercentage = (value, limit) => {
      if (limit === "∞") return "∞"; // Return infinity symbol for infinite limits
      return typeof value === "number" && value >= 0 ? `${Math.min(100, Math.round((value / limit) * 100))}%` : "N/A";
    };

    return [
      { title: "Data", dataCount: data.data ?? 0, limit: limitMap.data || 300, backgroundColor: "#1E90FF" },
      { title: "Data Models", dataCount: data.models ?? 0, limit: limitMap.models || 3, backgroundColor: "red" },
      { title: "Projects", dataCount: data.projects ?? 0, limit: limitMap.projects || 1, backgroundColor: "red" },
      { title: "Users", dataCount: companyMembers.length || 1, limit: limitMap.users || 3, backgroundColor: "green" },
      { title: "Storage", dataCount: data.storage ?? 0, limit: limitMap.storage || 300, suffix: "MB", backgroundColor: "#1E90FF" },
      { title: "Monthly AI Calls", dataCount: data.aiCall ?? 0, limit: limitMap.aiCall || 100, backgroundColor: "#1E90FF", percentage: "50%" },
    ].map((item) => ({
      ...item,
      percentage: item.percentage || getPercentage(Number(item.dataCount), item.limit),
    }));
  }, [data, companyMembers, theme]);

  return useMemo(() => (
    <YStack paddingHorizontal={compact ? 12 : 20} flex={1} gap={compact ? 8 : 16}>
      {/* Context Header */}
      {!compact && (
        <YStack marginBottom={16}>
          <Text style={{
            fontSize: 20,
            fontWeight: '700',
            color: !theme ? 'white' : 'rgba(0, 0, 0, 0.9)',
            marginBottom: 6,
            letterSpacing: '0.5px'
          }}>
            {isCompanyContext ? 'Company' : 'Personal'} Usage Statistics
          </Text>
          <Text style={{
            fontSize: 13,
            color: !theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.5)',
            fontWeight: '500'
          }}>
            {isCompanyContext
              ? `Showing usage for ${contextName}`
              : `Showing usage for ${contextName}`
            }
            {hasActiveSubscription && (
              <Text style={{ fontWeight: '600', color: 'rgba(34, 197, 94, 0.9)', marginLeft: 8 }}>
                • Active Subscription
              </Text>
            )}
          </Text>
        </YStack>
      )}

      {isFetching ? (
        <YStack alignItems="center" justifyContent="center" flex={1} minHeight={200}>
          <LottieView
            style={{ minHeight: 50, height: 50 }}
            source={require("@assets/animations/spin-leaf-loader.json")}
            autoPlay
            loop
          />
          <Text style={{ textAlign: "center", marginTop: 16, color: !theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
            Loading usage statistics...
          </Text>
        </YStack>
      ) : error ? (
        <YStack alignItems="center" justifyContent="center" flex={1} minHeight={200}>
            <Text style={{ textAlign: "center", color: !theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 0, 0, 0.6)' }}>
            Error loading usage statistics. Please try again.
          </Text>
        </YStack>
      ) : stats.length === 0 ? (
            <YStack alignItems="center" justifyContent="center" flex={1} minHeight={200}>
              <Text style={{ textAlign: "center", color: !theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                No usage data available
              </Text>
            </YStack>
      ) : (
            stats.map((stat, index) => {
              const isInfinite = stat.limit === "∞";

              // If user has active subscription, show counts instead of percentages
              const showCounts = hasActiveSubscription;

              let displayValue: string;
              let progressWidth: string;
              let progressValue: number;

              if (showCounts) {
                // Show counts for active subscription users
                displayValue = `${stat.dataCount}${stat.suffix || ''}`;
                if (isInfinite) {
                  displayValue = `${stat.dataCount} / ∞${stat.suffix || ''}`;
                }
                progressWidth = "100%"; // Full width for subscription users
                progressValue = 100;
              } else {
                // Show percentages for free users
                const percentageStr = typeof stat.percentage === 'string' ? stat.percentage : `${stat.percentage}%`;
                displayValue = isInfinite ? `${stat.dataCount} / ∞` : percentageStr;
                progressWidth = isInfinite ? "100%" : percentageStr;
                progressValue = isInfinite ? 100 : (typeof stat.percentage === 'number' ? stat.percentage : parseInt(percentageStr.replace('%', '')) || 0);
              }

              if (compact) {
                // Compact mode: inline label and percentage
                return (
                  <div key={index} className="progress-wrapper d-flex align-items-center flex-column" data-perc={displayValue} style={{ gap: 4 }}>
                    <XStack width="100%" justifyContent="space-between" alignItems="center" marginBottom={4}>
                      <Label style={{
                        fontSize: 12,
                        fontWeight: '700',
                        color: !theme ? 'white' : 'rgba(0, 0, 0, 0.85)',
                        letterSpacing: '0.2px',
                        margin: 0
                      }}>
                        {stat.title}
                      </Label>
                      <Text style={{
                        fontSize: 12,
                        fontWeight: '600',
                        color: !theme ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                        margin: 0
                      }}>
                        {displayValue}
                      </Text>
                    </XStack>
                    <div style={{ top: -20, height: '40px', position: 'relative', width: '100%', display: 'flex' }}>
                      <div className="progress mt-auto bg-primary-50" role="progressbar" aria-label={`${stat.title} usage`} aria-valuenow={progressValue} aria-valuemin={0} aria-valuemax={100} style={{ height: '6px', width: '100%', marginTop: 'auto' }}>
                        <div className="progress-bar animated-bar rounded-pill bg-primary-gradien overflow-visible" style={{ width: progressWidth }}>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              // Default mode: stacked label and floating percentage
              return (
                <div key={index} className="progress-wrapper d-flex align-items-center flex-column gap-4" data-perc={displayValue}>
                  <Label style={{
                    fontSize: 16,
                    fontWeight: '700',
                    textAlign: 'left',
                    width: '100%',
                    color: !theme ? 'white' : 'rgba(0, 0, 0, 0.85)',
                    marginBottom: 8,
                    letterSpacing: '0.3px'
                  }}>
                    {stat.title}
                  </Label>
              <div style={{ top: -30 }} className="h-50-px position-relative w-100 d-flex">
                <span
                  className="floating-label position-absolute text-xs fw-semibold text-secondary-light bg-base border radius-8 w-50-px h-32-px z-1 shadow d-flex justify-content-center align-items-center"
                  style={{
                    left: showCounts ? "50%" : (isInfinite ? "50%" : stat.percentage),
                    transform: "translateX(-50%)",
                    animationName: "animateFloatingLabel",
                    color: !theme ? 'white' : 'black'
                  }}
                >
                      {displayValue}
                </span>
                    <div className="progress mt-auto h-8-px w-100 bg-primary-50" role="progressbar" aria-label={`${stat.title} usage`} aria-valuenow={progressValue} aria-valuemin={0} aria-valuemax={100}>
                  <div className="progress-bar animated-bar rounded-pill bg-primary-gradien overflow-visible" style={{ width: progressWidth }}>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </YStack>
  ), [stats, isFetching, isCompanyContext, contextName, error, hasActiveSubscription, compact, theme]);
};

export default UsageStatMonitor;
